import { CalendarIcon } from '@/assets/icons'
import { countLabel } from '@/utils/plural'
import { PublicSectionGate } from '@components/PublicPage'
import { usePublicItinerary } from '@hooks'
import { fromUtcDate, toDashed } from '@lib/datetime'
import { TripHelper } from '@lib/TripHelper'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
	EmptyState,
	EmptyStateContainer,
	EmptyStateIcon,
	EmptyStateText,
	ItineraryPageContainer,
} from '../Itinerary/ItineraryPage.styles'
import { Itinerary } from '../Itinerary/types'
import { generateItinerary } from '../Itinerary/utils'
import PublicSectionLayout from '../PublicSections/PublicSectionLayout'
import ReadOnlyBoard from './ReadOnlyBoard'

/**
 * Public, read-only itinerary at `/shared/trips/:tripCode/itinerary` — the
 * original public share page, now framed by the same chrome as every other
 * public section (SiteHeader, back link to the shared overview, footer) with
 * the shared gate for loading / unavailable / error states. The itinerary
 * content itself (`ReadOnlyBoard` + the no-dates empty state) is untouched.
 */
const PublicItineraryPage: React.FC = () => {
	const { tripCode } = useParams<{ tripCode: string }>()
	const query = usePublicItinerary(tripCode)
	const [itinerary, setItinerary] = useState<Itinerary | null>(null)

	const data = query.data
	const trip = data?.trip
	const tripStartDate = trip?.startDate ? TripHelper.convertTripDateToInputFormat(trip.startDate) : ''
	const tripEndDate = trip
		? (() => {
				if (!trip.startDate || !trip.dayCount) return ''
				const tripHelper = new TripHelper(trip)
				const endDate = tripHelper.getEndDate()
				return endDate ? toDashed(fromUtcDate(endDate)) : ''
			})()
		: ''

	useEffect(() => {
		if (trip) {
			setItinerary(generateItinerary(trip, data?.events, data?.tripDayInfo))
		}
	}, [trip, data?.events, data?.tripDayInfo])

	return (
		<PublicSectionGate
			query={query}
			loadingLabel="Loading itinerary…"
			unavailableTitle="Itinerary Unavailable"
			unavailableMessage="This itinerary doesn't exist or has been set to private by the trip owner."
		>
			{({ trip: gatedTrip, travelSegments }) => (
				<PublicSectionLayout
					// Data present implies the query ran, which implies the code param existed.
					tripCode={tripCode ?? ''}
					title="Itinerary"
					subtitle={`${countLabel(gatedTrip.dayCount, 'day')}`}
				>
					<ItineraryPageContainer>
						{!itinerary ? (
							<EmptyStateContainer>
								<EmptyState>
									<EmptyStateIcon>
										<CalendarIcon sx={{ fontSize: 128 }} />
									</EmptyStateIcon>
									<EmptyStateText>This trip does not have itinerary dates yet.</EmptyStateText>
								</EmptyState>
							</EmptyStateContainer>
						) : (
							<ReadOnlyBoard
								itinerary={itinerary}
								tripName={gatedTrip.name}
								dayCount={gatedTrip.dayCount}
								tripStartDate={tripStartDate}
								tripEndDate={tripEndDate}
								travelSegments={travelSegments}
							/>
						)}
					</ItineraryPageContainer>
				</PublicSectionLayout>
			)}
		</PublicSectionGate>
	)
}

export default PublicItineraryPage
